import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import {
  TRADING_SEGMENTS,
  COMMON_SCRIPTS,
  ENTITY_TYPES,
} from "../../utils/constant";
import { formatEntityName } from "../../utils/helper";
import SearchableSelect from "../Common/SearchableSelect";

const BlockScriptsLayer = () => {
  const { AsyncGetApiCall, AsyncPostApiCall, AsyncDeleteApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  // Dynamic Lists
  const [entities, setEntities] = useState([]);
  const [scriptsList, setScriptsList] = useState([]);
  const [dynamicSegments, setDynamicSegments] = useState([]);

  // Selection State
  const [selection, setSelection] = useState({
    accountType: "",
    entityId: "",
    segment: "",
    script: "",
  });

  const [filters, setFilters] = useState({
    segment: "",
    search: "",
  });

  const fetchBlockedScripts = useCallback(async () => {
    setLoading(true);
    const queryParams = {
      accountType: selection.accountType === "All" ? "" : selection.accountType,
      entityId: selection.entityId,
      segment: filters.segment,
      search: filters.search,
    };
    const result = await AsyncGetApiCall(
      "/trading-settings/script-block/list",
      queryParams,
    );
    if (result?.success) {
      setData(result.data || []);
    }
    setLoading(false);
  }, [AsyncGetApiCall, selection.accountType, selection.entityId, filters]);

  useEffect(() => {
    fetchBlockedScripts();
  }, [fetchBlockedScripts]);

  const fetchEntities = async (type) => {
    if (!type || type === "All") {
      setEntities([]);
      return;
    }

    // Map account type to dynamic list type
    let listType = type.toLowerCase();
    if (listType === "subadmin") listType = "subadmin";

    const result = await AsyncGetApiCall("/users/dynamic-list", { listType });
    if (result?.success) setEntities(result.data || []);
  };

  const fetchSegments = async () => {
    const result = await AsyncGetApiCall("/trading-settings/segments/list");
    const dataArr = Array.isArray(result) ? result : result?.data || [];
    setDynamicSegments(dataArr);
  };

  const fetchScripts = async (segment) => {
    if (!segment) {
      setScriptsList([]);
      return;
    }
    const result = await AsyncGetApiCall(
      `/trading-settings/scripts?segment=${segment}&limitVal=1000`,
    );
    if (result?.success) {
      setScriptsList(result.data || []);
    }
  };

  useEffect(() => {
    // fetchBlockedScripts(); // Removing this as it's triggered by deps above
  }, []);

  useEffect(() => {
    fetchSegments();
  }, []);

  const handleAccountTypeChange = (e) => {
    const type = e.target.value;
    if (type === "All") {
      setSelection({
        ...selection,
        accountType: type,
        entityId: "all",
      });
      setEntities([]);
    } else {
      // Default to "all" (All Users/Brokers/etc.) when switching type
      setSelection({ ...selection, accountType: type, entityId: "all" });
      fetchEntities(type);
    }
  };

  const handleSegmentChange = (e) => {
    const seg = e.target.value;
    if (seg === "all") {
      setSelection({ ...selection, segment: seg, script: "all" });
      setScriptsList([]);
    } else {
      setSelection({ ...selection, segment: seg, script: "" });
      fetchScripts(seg);
    }
  };

  const handleToggleBlock = async (isBlocked) => {
    if (!selection.segment) {
      toast.error("Please select a segment");
      return;
    }
    if (selection.segment !== "all" && !selection.script) {
      toast.error("Please select a script");
      return;
    }
    if (!selection.accountType) {
      toast.error("Please select an entity type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        entityType:
          selection.accountType === "All"
            ? "all"
            : selection.accountType.toLowerCase(),
        entityId: selection.entityId || "all",
        segment: selection.segment,
        script: selection.script || "all",
        isBlocked,
      };

      const result = await AsyncPostApiCall(
        "/trading-settings/script-block/bulk",
        payload,
      );

      if (result?.success) {
        toast.success(result.message || "Action processed successfully");
        setSelection({
          ...selection,
          segment: "",
          script: "",
        });
        setScriptsList([]);
        fetchBlockedScripts();
      } else {
        toast.error(result?.message || "Failed to update");
      }
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleRowStatusToggle = async (item) => {
    setLoading(true);
    const payload = {
      segment: item.segment,
      script: item.script,
      isBlocked: !item.isBlocked,
    };
    if (item.userId) payload.userId = item.userId._id;
    if (item.brokerId) payload.brokerId = item.brokerId._id;
    if (item.subAdminId) payload.subAdminId = item.subAdminId._id;

    const result = await AsyncPostApiCall(
      "/trading-settings/script-block/toggle",
      payload,
    );
    if (result?.success) {
      toast.success(!item.isBlocked ? "Script Blocked" : "Script Unblocked");
      fetchBlockedScripts();
    } else {
      toast.error(result?.message || "Failed to update status");
    }
    setLoading(false);
  };

  const handleRemove = async (id) => {
    setLoading(true);
    const result = await AsyncDeleteApiCall(
      `/trading-settings/script-block/${id}`,
    );
    if (result?.success) {
      toast.success("Entry removed");
      fetchBlockedScripts();
    }
    setLoading(false);
  };

  const handleExportCSV = () => {
    if (data.length === 0) {
      toast.warn("No data to export");
      return;
    }
    const headers = [
      "Entity Type",
      "Login ID",
      "Name",
      "Segment",
      "Script",
      "Status",
    ];
    const csvContent = [
      headers.join(","),
      ...data.map((item) => {
        const type = item.userId
          ? "User"
          : item.brokerId
          ? "Broker"
          : item.subAdminId
          ? "SubAdmin"
          : "Global";
        const entity = item.userId || item.brokerId || item.subAdminId;
        const loginId = entity?.loginId || "All";
        const name = entity?.name || entity?.masterName || "All";
        const status = item.isBlocked ? "Blocked" : "Allowed";

        return [type, loginId, name, item.segment, item.script, status].join(
          ",",
        );
      }),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "blocked_scripts_list.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {loading && <Loader />}

      <div className="card radius-16 border-0 shadow-sm mb-24">
        <div className="card-body p-24">
          <div className="row g-2 align-items-end mb-24">
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light">
                ENTITY TYPE
              </label>
              <SearchableSelect
                placeholder="Select Type"
                value={selection.accountType}
                onChange={handleAccountTypeChange}
                options={ENTITY_TYPES.map((type) => ({
                  label: type,
                  value: type,
                }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label text-xs fw-bold text-secondary-light">
                ACCOUNT (LOGIN ID)
              </label>
              <SearchableSelect
                placeholder={
                  !selection.accountType
                    ? "Select Entity First"
                    : selection.accountType === "All"
                    ? "Global (All Entities)"
                    : `All ${selection.accountType}s`
                }
                disabled={!selection.accountType || selection.accountType === "All"}
                value={selection.entityId}
                onChange={(e) =>
                  setSelection({ ...selection, entityId: e.target.value })
                }
                options={[
                  {
                    label: `All ${selection.accountType || ""}s`,
                    value: "all",
                  },
                  ...entities.map((en) => ({
                    label: formatEntityName(
                      en.loginId,
                      en.name || en.masterName,
                    ),
                    value: en._id,
                  })),
                ]}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label text-xs fw-bold text-secondary-light">
                SEGMENT
              </label>
              <SearchableSelect
                placeholder="Select Segment"
                value={selection.segment}
                onChange={handleSegmentChange}
                options={[
                  { label: "All Segments", value: "all" },
                  ...dynamicSegments.map((s) => ({
                    label: s.displayName,
                    value: s.name,
                  })),
                ]}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label text-xs fw-bold text-secondary-light">
                SCRIPT
              </label>
              <SearchableSelect
                placeholder={
                  selection.segment === "all" ? "All Scripts" : "Select Script"
                }
                options={
                  selection.segment === "all"
                    ? [{ label: "All Scripts", value: "all" }]
                    : [
                        { label: "All Scripts", value: "all" },
                        ...scriptsList.map((s) => ({
                          label: `${s.name} (${s.exchange})`,
                          value: s.name,
                        })),
                      ]
                }
                value={selection.script || (selection.segment === "all" ? "all" : "")}
                onChange={(e) =>
                  setSelection({ ...selection, script: e.target.value })
                }
                disabled={!selection.segment || selection.segment === "all"}
              />
            </div>
            <div className="col-md-2">
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-danger radius-8 w-100 py-10"
                  onClick={() => handleToggleBlock(true)}
                >
                  Block
                </button>
                <button
                  className="btn btn-sm btn-success radius-8 w-100 py-10"
                  onClick={() => handleToggleBlock(false)}
                >
                  Unblock
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* List Table */}
      <div className="card radius-16 border-0 shadow-sm mb-24">
        <div className="card-header bg-primary-600 py-12 px-16 d-flex align-items-center justify-content-between" style={{ borderRadius: "16px 16px 0 0", overflow: "visible" }}>
          <h6 className="text-white mb-0 text-sm">
            Blocked / Allowed Scripts List ({data.length})
          </h6>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-success radius-8 d-flex align-items-center gap-1"
              onClick={handleExportCSV}
            >
              <Icon icon="lucide:download" /> CSV
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-white radius-8 d-flex align-items-center gap-1"
              onClick={() =>
                setFilters({
                  segment: "",
                  search: "",
                })
              }
              title="Reset Filters"
            >
              <Icon icon="lucide:rotate-ccw" />
            </button>
            <div className="flex-grow-1" style={{ minWidth: "150px" }}>
              <SearchableSelect
                placeholder="All Segments"
                value={filters.segment}
                onChange={(e) =>
                  setFilters({ ...filters, segment: e.target.value })
                }
                options={dynamicSegments.map((s) => ({
                  label: s.displayName,
                  value: s.name,
                }))}
              />
            </div>
            <div className="flex-grow-1" style={{ minWidth: "150px" }}>
              <input
                type="text"
                className="form-control form-control-sm radius-8"
                placeholder="Search scripts..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
              />
            </div>
          </div>
        </div>
        <div className="table-responsive">
          <table className="table bordered-table mb-0 sm-table">
            <thead>
              <tr>
                <th>ACCOUNT TYPE</th>
                <th>LOGIN ID / NAME</th>
                <th>SEGMENT</th>
                <th>SCRIPT</th>
                <th>STATUS</th>
                <th className="text-center">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => (
                <tr key={item._id}>
                  <td>
                    {item.userId
                      ? "User"
                      : item.brokerId
                        ? "Broker"
                        : item.subAdminId
                          ? "SubAdmin"
                          : "Global"}
                  </td>
                  <td>
                    {item.userId
                      ? formatEntityName(item.userId.loginId, item.userId.name)
                      : item.brokerId
                        ? formatEntityName(
                            item.brokerId.loginId,
                            item.brokerId.name,
                          )
                        : item.subAdminId
                          ? formatEntityName(
                              item.subAdminId.loginId,
                              item.subAdminId.masterName,
                            )
                          : "All"}
                  </td>
                  <td>{item.segment}</td>
                  <td>{item.script}</td>
                  <td>
                    <span
                      className={`badge ${item.isBlocked ? "bg-danger-focus text-danger-600" : "bg-success-focus text-success-600"}`}
                    >
                      {item.isBlocked ? "Blocked" : "Allowed"}
                    </span>
                  </td>
                  <td className="text-center space-x-2">
                    <button
                      className={`btn btn-sm ${item.isBlocked ? "text-success" : "text-warning"}`}
                      title={item.isBlocked ? "Allow" : "Block"}
                      onClick={() => handleRowStatusToggle(item)}
                    >
                      <Icon
                        icon={
                          item.isBlocked
                            ? "lucide:check-circle"
                            : "lucide:slash"
                        }
                        size={18}
                      />
                    </button>
                    <button
                      className="btn btn-sm text-danger"
                      title="Remove"
                      onClick={() => handleRemove(item._id)}
                    >
                      <Icon icon="lucide:trash-2" size={18} />
                    </button>
                  </td>
                </tr>
              ))}
              {data.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    No entries found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default BlockScriptsLayer;
